import { addMinutes, startOfHour, addHours, differenceInMinutes } from 'date-fns';
import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import handleException from '@/common/utils/errors';

export type PomodoroMode = '25/5' | '50/10';

interface PomodoroState {
  mode: PomodoroMode;
  currentPhase: 'focus' | 'break';
  nextPhaseTime: Date | null;
  targetHourEnd: Date | null;
  isLoading: boolean;

  setMode: (mode: PomodoroMode, userId?: string) => void;
  switchPhase: () => void;
  loadUserPreferences: (userId: string) => Promise<void>;
  saveUserPreferences: (userId: string, mode: PomodoroMode) => Promise<void>;
}

const MODES = {
  '25/5': { focus: 25, break: 5 },
  '50/10': { focus: 50, break: 10 },
};

const calculateInitialPhase = (now: Date, mode: PomodoroMode): { phase: 'focus' | 'break'; nextPhaseTime: Date; targetHourEnd: Date } => {
  const currentHour = startOfHour(now);
  const minutesIntoHour = differenceInMinutes(now, currentHour);
  const { focus, break: breakTime } = MODES[mode];
  const cycleTime = focus + breakTime;
  const cyclesCompleted = Math.floor(minutesIntoHour / cycleTime);
  const minutesIntoCycle = minutesIntoHour % cycleTime;

  return {
    phase: minutesIntoCycle < focus ? 'focus' : 'break',
    nextPhaseTime: addMinutes(currentHour, (cyclesCompleted * cycleTime) + (minutesIntoCycle < focus ? focus : cycleTime)),
    targetHourEnd: addHours(currentHour, 1),
  };
};

export const usePomodoroStore = create<PomodoroState>((set, get) => ({
  mode: '25/5',
  currentPhase: 'focus',
  nextPhaseTime: null,
  targetHourEnd: null,
  isLoading: false,

  saveUserPreferences: async (userId: string, mode: PomodoroMode) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert(
          {
            user_id: userId,
            pomodoro_mode: mode
          },
          { onConflict: 'user_id' }
        );

      if (error) {
        handleException(error);
      }
    } catch (error) {
      handleException(error);
    }
  },

  setMode: (mode, userId) => {
    const now = new Date();
    const { phase, nextPhaseTime, targetHourEnd } = calculateInitialPhase(now, mode);

    set({
      mode,
      currentPhase: phase,
      nextPhaseTime,
      targetHourEnd,
    });

    // Save to Supabase if userId is provided
    if (userId) {
      get().saveUserPreferences(userId, mode);
    }
  },

  switchPhase: () => {
    const { mode, currentPhase, nextPhaseTime, targetHourEnd } = get();
    if (!nextPhaseTime || !targetHourEnd) return;

    const newPhase = currentPhase === 'focus' ? 'break' : 'focus';
    const modeConfig = MODES[mode];
    const duration = newPhase === 'focus' ? modeConfig.focus : modeConfig.break;
    const newNextPhaseTime = addMinutes(nextPhaseTime, duration);

    // If next phase would go beyond the target hour, recalculate from next hour
    if (newNextPhaseTime > targetHourEnd) {
      const { phase, nextPhaseTime, targetHourEnd: newTargetHourEnd } = calculateInitialPhase(targetHourEnd, mode);
      set({
        currentPhase: phase,
        nextPhaseTime,
        targetHourEnd: newTargetHourEnd,
      });
      return;
    }

    set({
      currentPhase: newPhase,
      nextPhaseTime: newNextPhaseTime,
    });
  },

  loadUserPreferences: async (userId: string) => {
    set({ isLoading: true });

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('pomodoro_mode')
        .eq('user_id', userId)
        .single();

      if (error) {
        handleException(error);
        if(error.code === "PGRST116"){// non existent preference error
          get().setMode('25/5', userId);
        }
        return;
      }

      if (data?.pomodoro_mode) {
        get().setMode(data.pomodoro_mode as PomodoroMode);
      }
    } finally {
      set({ isLoading: false });
    }
  },
}));
